var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import axios from 'axios';
import { uuid } from 'uuidv4';
import { DOCUMENT_STATUSES } from 'components/modules/documents/Document';
import { GET_DOCUMENT_METADATA, GET_DOCUMENT_TYPES, GET_DOCUMENTS, GET_SIGNED_URL, GET_SIGNED_URLS, GET_UNIQUE_LATEST_DOCUMENTS, GET_DOCUMENTS_V2, GET_UNIQUE_LATEST_DOCUMENTS_V2, } from 'utils/graphQL/queries/documentService';
import { ATTACH_DOCUMENTS, DELETE_DOCUMENTS, PROCESS_DOCUMENT, UPDATE_DOCUMENT, UPLOAD_DOCUMENTS, } from 'utils/graphQL/mutations/documentService';
import { getGraphQLClient } from 'utils/graphQL/client';
import { DOCUMENT_TYPES_KEY } from 'constants/Documents';
import moment from 'moment';
import { isPDF, validateDocumentFilesSizes, validatePDFEncryption, } from './documents';
import { useQuery } from '@tanstack/react-query';
export const GET_DOCUMENTS_LIMIT = 50;
export const DOCUMENT_SIZE_LIMIT_MB = 25;
export const DOCUMENT_SIZE_LIMIT = '2.5e+7';
export const RecognitionFieldTypes = {
    GENERIC_UPLOAD_POD: 'GENERIC_UPLOAD_POD',
};
const DocumentsClient = getGraphQLClient({
    url: DOCUMENT_SERVICE_URL,
});
export async function getDocumentsV2(foreignKeys, types, options = {}, groupTypes = [], excludeTypes = []) {
    if (types) {
        types = Array.isArray(types) ? types : [types];
    }
    try {
        return await DocumentsClient.query({
            query: GET_DOCUMENTS_V2,
            variables: {
                query: {
                    foreignKeys,
                    types,
                    groupTypes,
                    excludeTypes,
                },
                options: Object.assign({ limit: GET_DOCUMENTS_LIMIT }, options),
            },
        });
    }
    catch (e) {
        console.error('Error during getDocumentsV2: ', e.message);
        return { errors: [e] };
    }
}
export async function getUniqueLatestDocumentsV2(foreignKeys, types) {
    try {
        return await DocumentsClient.query({
            query: GET_UNIQUE_LATEST_DOCUMENTS_V2,
            variables: {
                query: {
                    types,
                    foreignKeys,
                },
            },
        });
    }
    catch (e) {
        console.error('Error during getUniqueLatestDocumentsV2: ', e.message);
        return { errors: [e] };
    }
}
export async function getDocuments(foreignKeys, types, options = {}, groupTypes = [], excludeTypes = []) {
    if (types) {
        types = Array.isArray(types) ? types : [types];
    }
    try {
        return await DocumentsClient.query({
            query: GET_DOCUMENTS,
            variables: {
                query: {
                    foreignKeys,
                    types,
                    groupTypes,
                    excludeTypes,
                },
                options: Object.assign({ limit: GET_DOCUMENTS_LIMIT }, options),
            },
        });
    }
    catch (e) {
        console.error('Error during getDocuments: ', e.message);
        return { errors: [e] };
    }
}
export async function getUniqueLatestDocuments(foreignKeys, types) {
    try {
        return await DocumentsClient.query({
            query: GET_UNIQUE_LATEST_DOCUMENTS,
            variables: {
                query: {
                    types,
                    foreignKeys,
                },
            },
        });
    }
    catch (e) {
        console.error('Error during getUniqueLatestDocuments: ', e.message);
        return { errors: [e] };
    }
}
export async function getDocument(key) {
    try {
        return await DocumentsClient.query({
            query: GET_DOCUMENTS,
            variables: {
                query: { key },
                options: {},
            },
        });
    }
    catch (e) {
        console.error('Error during getDocument: ', e.message);
        return { errors: [e] };
    }
}
export function attachDocuments(documentsIds, foreignKeys, uploadType = 'SINGLE', allowAudit = false) {
    return DocumentsClient.mutate({
        mutation: ATTACH_DOCUMENTS,
        variables: {
            data: { ids: documentsIds, foreignKeys, uploadType, allowAudit },
        },
    });
}
export async function getSignedUrl(key) {
    try {
        if (!key) {
            console.error('Error during getSignedUrl: key must be not empty.');
            return { data: null, errors: null };
        }
        return await DocumentsClient.query({
            query: GET_SIGNED_URL,
            variables: { query: { key } },
        });
    }
    catch (e) {
        console.error('Error during getSignedUrl: ', e.message);
        return { errors: [e] };
    }
}
export function getSignedUrls(foreignKeys) {
    try {
        return DocumentsClient.query({
            query: GET_SIGNED_URLS,
            variables: { query: { foreignKeys } },
        });
    }
    catch (e) {
        console.error('Error during getSignedUrls: ', e.message);
        return { errors: [e] };
    }
}
export function processDocument(documentId, shipmentId, stopId, recognitionOptions) {
    return DocumentsClient.mutate({
        mutation: PROCESS_DOCUMENT,
        variables: {
            data: {
                documentId,
                shipmentId,
                stopId: stopId !== null && stopId !== void 0 ? stopId : null,
                recognitionOptions,
            },
        },
    });
}
export async function uploadDocuments(documents) {
    try {
        validateDocumentFilesSizes(documents);
        const pdfDocuments = documents.filter((document) => isPDF(document.contentType));
        await validatePDFEncryption(pdfDocuments.map((document) => document.content));
        const documentsWithContentId = documents.map((document) => {
            return Object.assign(Object.assign({}, document), { documentContentId: uuid() });
        });
        const { data, errors: uploadErrors } = await DocumentsClient.mutate({
            mutation: UPLOAD_DOCUMENTS,
            variables: {
                data: documentsWithContentId.map((_a) => {
                    var { content, size } = _a, document = __rest(_a, ["content", "size"]);
                    return document;
                }),
            },
        });
        if (uploadErrors) {
            return {
                error: uploadErrors[0],
            };
        }
        const presignedUrlsWithContent = data.uploadDocuments.map(({ presignedUrl, documentContentId, key, id, contentType }) => {
            const { content, size } = documentsWithContentId.find((documentWithContentId) => documentWithContentId.documentContentId === documentContentId);
            return {
                presignedUrl,
                contentType,
                content,
                key,
                id,
                size,
            };
        });
        const documentsUploadedViaSignedUrl = await Promise.all(presignedUrlsWithContent.map(async ({ presignedUrl, content, contentType, key, id, size }) => {
            try {
                const axiosResponse = await axios.put(presignedUrl, content, {
                    headers: {
                        'Content-Type': contentType,
                    },
                });
                if (axiosResponse.status === 200) {
                    await DocumentsClient.mutate({
                        mutation: UPDATE_DOCUMENT,
                        variables: {
                            docKey: key,
                            payload: {
                                status: DOCUMENT_STATUSES.UPLOADED,
                                size,
                            },
                        },
                    });
                    return {
                        key,
                    };
                }
            }
            catch (error) {
                console.log('Error during uploading document to S3 using presigned url', error.message);
                await DocumentsClient.mutate({
                    mutation: DELETE_DOCUMENTS,
                    variables: { data: { ids: [id] } },
                });
                return {
                    error,
                };
            }
        }));
        const successfullyUploadedDocuments = data.uploadDocuments.filter((document) => documentsUploadedViaSignedUrl.some(({ key }) => key === document.key));
        if (!successfullyUploadedDocuments.length) {
            const uploadError = documentsUploadedViaSignedUrl.find(({ error }) => error);
            return {
                error: uploadError,
            };
        }
        const uploadDocumentsForResponse = successfullyUploadedDocuments.map((_a) => {
            var { presignedUrl, documentContentId } = _a, doc = __rest(_a, ["presignedUrl", "documentContentId"]);
            return Object.assign(Object.assign({}, doc), { status: DOCUMENT_STATUSES.UPLOADED });
        });
        return {
            data: { uploadDocuments: uploadDocumentsForResponse },
        };
    }
    catch (error) {
        console.log('Error during uploading document', error.message);
        return {
            error,
        };
    }
}
export async function waitDocumentUpload(key) {
    const documentData = await getDocument(key);
    const document = documentData.data.getDocuments.documents[0];
    if (document && document.status === 'PENDING') {
        await new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
        return waitDocumentUpload(key);
    }
}
export async function getDocumentMetadata(documentId) {
    try {
        return await DocumentsClient.query({
            query: GET_DOCUMENT_METADATA,
            variables: { query: { documentId } },
        });
    }
    catch (e) {
        console.error('Error during getDocumentMetadata: ', e.message);
        return { errors: [e] };
    }
}
export async function getDocumentTypes(listType, showListType) {
    try {
        const storedDocTypes = localStorage.getItem(`${listType.toUpperCase()}_${DOCUMENT_TYPES_KEY}_${showListType}`);
        if (storedDocTypes) {
            const docTypes = JSON.parse(storedDocTypes);
            // check if stored data has been expired with term 1hr
            if (moment().isBefore(moment(docTypes.cachedAt).add(1, 'hour'))) {
                return Promise.resolve(docTypes.value);
            }
        }
        const res = await DocumentsClient.query({
            query: GET_DOCUMENT_TYPES,
            variables: {
                query: Object.assign({ listType }, (showListType !== undefined && { showListType })),
            },
        });
        localStorage.setItem(`${listType.toUpperCase()}_${DOCUMENT_TYPES_KEY}_${showListType}`, JSON.stringify({
            value: res,
            cachedAt: moment(),
        }));
        return res;
    }
    catch (e) {
        console.error('Error during getDocumentTypes: ', e.message);
        return { errors: [e] };
    }
}
export function useGetDocumentTypes({ listType, showListType }) {
    const query = useQuery({
        queryKey: ['invoiceDocumentTypes', listType],
        queryFn: () => {
            return getDocumentTypes(listType, showListType);
        },
        enabled: !!listType,
    });
    const { data, error, isLoading, isError, isFetching, isSuccess, status, refetch, } = query;
    return {
        data: (data === null || data === void 0 ? void 0 : data.data) || {},
        error,
        isLoading,
        isError,
        isFetching,
        isSuccess,
        status,
        refetch,
    };
}
